import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  FilterIconStorygame,
  RefreshIcon,
  CloseIconStoryGame,
} from 'public/assets';
import { AiProfileInfo, OnboardingAiProfileType } from '@/customTypes/chapter';
import useRewardedAd, { AD_UNIT_ID, AdStatus } from '@/hooks/useRewardedAd';
import StoryGameAiImageCropPopup from './StoryGameAiImageCropPopup';
import {
  MainCharacter,
  PromptFilterOption,
  RequestCreateAICharacter,
} from '@/customTypes/storygame';
import { SPLoading, Toast } from '@/components/commons';
import { useGetNoticeTermsQuery } from '@/operations/queries/user/__generated__/useGetNoticeTerms.generated';
import { useRouter } from 'next/router';
import StoryGameUploadErrorToast from '@/components/commons/popup/StoryGameUploadErrorToast';
import { ApolloError } from '@apollo/client';
import { insertCommas, isMobileDevice } from '@/common/utils';
import {
  showRewardAdForApp,
  goExternalPageForApp,
  onPurchaseForApp,
} from '@/lib/webview';
import theme from '@/styles/theme';
import { IStoryGameOnboardingAlert } from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import { IS_IOS_SAFARI } from '@/lib/common-react/utils/environment';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/store';
import { shallowEqual } from 'react-redux';
import { adFinish, adStart } from '@/lib/eventManager';
import { Challenge_Story_Genre } from '@/baseType';
import { AdLocationType, ContentType } from '@/customTypes/event';
import useTranslation from 'next-translate/useTranslation';
import { SERVER_ERROR_CODE, STORAGE_KEY } from '@/common/values';
import { useEnsureTossPaymentsDoneQuery } from '@/operations/queries/chapter/__generated__/useEnsureTossPaymentsDone.generated';
import { UserInfo } from '@/customTypes/user';

function areAllValuesEmpty(obj: Record<string, any>): boolean {
  return Object.values(obj).every(
    (value) => value === undefined || value === null || value === '',
  );
}

interface FilterState {
  [key: string]: number | undefined;
}

const MY_CHAR_APP_PRICE = {
  KWR: 500,
  USD: 0.39,
};

const StoryGameAiImageFilterPopup = (props: {
  aiProfileInfo?: AiProfileInfo;
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onCreateAiCharacter: (data: RequestCreateAICharacter) => void;
  aiCharacterPromptOptions?: PromptFilterOption;
  selectedCharacter?: MainCharacter; // 좌측 캐릭터 선택
  price?: number;
  setStorygameToastData: (data: {
    message: string;
    isVisible: boolean;
  }) => void;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  onGetAiCharacterPromptOptions: () => Promise<void>;
  paymentProduct?: {
    inappProductId?: string;
    inappCoinId?: number;
    amount?: number;
    price?: number;
    originalPrice?: number;
    originalPriceFloat?: number | null;
    priceFloat: number;
  };
}) => {
  const {
    aiProfileInfo,
    setAiProfileType,
    onChangeAiProfile,
    onCreateAiCharacter,
    aiCharacterPromptOptions,
    onGetAiCharacterPromptOptions,
    selectedCharacter,
    setStorygameToastData,
    paymentProduct,
  } = props;

  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  const {
    isAdReady,
    showAd,
    status,
    requestNewAdWithAuto,
    retryCount,
    stopAutoRequest,
  } = useRewardedAd(false, () => setIsAdReward(true)); // 자동 요청 기능을 켠 상태로 시작
  const router = useRouter();
  const [isAdReward, setIsAdReward] = useState<boolean>(false);

  const { data } = useGetNoticeTermsQuery({
    skip: router.pathname.includes('webview'),
  });

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isShowErrorMessage, setIsShowErrorMessage] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterState>({
    얼굴: undefined,
    체형: undefined,
    어깨: undefined,
    근육: undefined,
    가슴: undefined,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [adButtonText, setAdButtonText] = useState(
    t('storygame_ai_onboarding_popup_button_waiting_for_ad'),
  );
  const [isEnableAdButton, setIsEnableAdButton] = useState(false);
  const isLoadStartedAd = useRef(false);
  const [toastText, setToastText] = useState<string | undefined>();

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilterChange = (category: string, value: number) => {
    // value가 같은게 눌리면 제거
    if (filter[category] === value) {
      setFilter((prevFilter: FilterState) => ({
        ...prevFilter,
        [category]: undefined,
      }));
      return;
    }
    setFilter((prevFilter: FilterState) => ({
      ...prevFilter,
      [category]: value,
    }));
  };
  const onClose = useCallback(() => {
    setAiProfileType(undefined);
  }, [setAiProfileType]);
  const paymentPageRef = useRef<Window | null>(null);
  const [requestedTossPaymentsId, setRequestedTossPaymentsId] =
    useState<string>();
  const [upcId, setUpcId] = useState<string>();
  const [userInfo, setUserInfo] = useState<UserInfo>();

  useEffect(() => {
    const storageInfo = localStorage.getItem(STORAGE_KEY.USER_PROPERTIES);
    const parsedInfo = storageInfo && JSON.parse(storageInfo);
    setUserInfo(parsedInfo);
  }, [router.locale]);

  const { data: tossPaymentsCompletedData, stopPolling } =
    useEnsureTossPaymentsDoneQuery({
      variables: {
        data: {
          id: Number(requestedTossPaymentsId),
        },
      },
      pollInterval: 1000,
      skip: !requestedTossPaymentsId,
      onCompleted: (data) => {
        if (data.ensureTossPaymentsDone.ok === 1) {
          stopPolling();
          onCreateAiCharacterSubmit();
        }
      },
    });

  const handlePaymentPageMessage = (event: MessageEvent) => {
    if (event.data.type === 'setTossPaymentsId') {
      setRequestedTossPaymentsId(event.data.parameter.tossPaymentsId);
    }
  };

  const paymentAiCharacter = () => {
    if (isMobileDevice()) {
      paymentProduct?.inappProductId &&
        onPurchaseForApp({
          productId: paymentProduct?.inappProductId,
        });
    } else {
      const inappCoinId = paymentProduct?.inappCoinId;
      const storyId = playChapterInfo?.playChapter.story.storyId;
      const url = `/payment?inappCoinIdForMyChar=${inappCoinId}&url=/story/${storyId}&type=close`;

      paymentPageRef.current = window.open(url, '_blank');
      paymentPageRef.current?.addEventListener(
        'message',
        handlePaymentPageMessage,
      );
    }
  };

  const onCreateAiCharacterSubmit = useCallback(
    async (param?: { showAd?: boolean; isFree?: boolean }) => {
      const { showAd, isFree } = param || {};
      if (loading || !isChecked) return;
      if (aiProfileInfo?.cropImageFile) {
        if (showAd) {
          sendAdEvent(false);
        }
        setLoading(true);
        try {
          await onCreateAiCharacter({
            image: aiProfileInfo?.cropImageFile,
            additionalPromptIds: Object.values(filter).filter(
              (d) => d,
            ) as number[],
            viewAd: !!showAd,
            tossPaymentsId: requestedTossPaymentsId
              ? Number(requestedTossPaymentsId)
              : undefined,
            upcId: upcId ? Number(upcId) : undefined,
          });
          setRequestedTossPaymentsId(undefined);
          setUpcId(undefined);

          if (showAd) {
            setIsAdReward(false);
            onChangeAiProfile({
              usedAd: (aiProfileInfo?.usedAd ?? 0) + 1,
            });
          } else if (isFree) {
            setStorygameToastData({
              message: t(
                'storygame_ai_onboarding_popup_description_generate_free_once',
              ),
              isVisible: true,
            });
            onChangeAiProfile({ latestCreatedAt: new Date().toISOString() });
          }
          onClose();
        } catch (e) {
          if (
            (e as ApolloError).message.includes(
              SERVER_ERROR_CODE.AI_CHR_TOSS_PAYMENTS_REWARD_ALREADY_CLAIMED,
            )
          ) {
            // 코인 부족 에러(결제 실패)발생 시, 재 결제를 위해 tossPaymentsId를 초기화
            setRequestedTossPaymentsId(undefined);
          }

          setErrorMessage(
            t('storygame_ai_onboarding_screen_description_error_occurred'),
          );
          setIsShowErrorMessage(true);
        } finally {
          setLoading(false);
        }
      }
    },
    [
      aiProfileInfo?.cropImageFile,
      aiProfileInfo?.usedAd,
      filter,
      isChecked,
      loading,
      onChangeAiProfile,
      onClose,
      onCreateAiCharacter,
      status,
    ],
  );

  const handleAdStatusUpdate = (
    rewarded: boolean, // <리워드 여부>
    type?: string, // <요청한 광고가 맞는지 확인하기 위한 타입>
  ) => {
    if (rewarded) {
      onCreateAiCharacterSubmit({ showAd: true });
    }
  };

  const completePurchaseInApp = (upcId: string, success: boolean) => {
    if (success) {
      setUpcId(upcId);
      onCreateAiCharacterSubmit();
    } else {
      setUpcId(undefined);
    }
  };

  useEffect(() => {
    if (aiProfileInfo?.cropImageFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(aiProfileInfo?.cropImageFile);
    }
  }, [aiProfileInfo?.cropImageFile]);

  useEffect(() => {
    onGetAiCharacterPromptOptions();
  }, [selectedCharacter, onGetAiCharacterPromptOptions]);

  useEffect(() => {
    // 보상을 받았을 때 광고를 닫으면 실행
    if (status === AdStatus.CLOSED && isAdReward) {
      onCreateAiCharacterSubmit({ showAd: true });
    }
  }, [status, onCreateAiCharacterSubmit, isAdReward]);

  useEffect(() => {
    window.updateAdStatus = handleAdStatusUpdate;
    window.completePurchase = completePurchaseInApp;

    return () => {
      delete window.updateAdStatus;
      delete window.completePurchase;
    };
  }, [aiProfileInfo, isChecked]);

  useEffect(() => {
    const isRemainAdCount =
      (aiProfileInfo?.usedAd ?? 0) < (aiProfileInfo?.totalAd ?? 0);

    if (aiProfileInfo?.latestCreatedAt || !areAllValuesEmpty(filter)) {
      if (isRemainAdCount) {
        if (isMobileDevice()) {
          setAdButtonText(
            t('storygame_ai_onboarding_popup_button_watch_ad_then_generate'),
          );
          if (isChecked) {
            setIsEnableAdButton(true);
          }
        } else {
          if (!isLoadStartedAd.current) {
            isLoadStartedAd.current = true;
            requestNewAdWithAuto();
          } else if (isAdReady) {
            setAdButtonText(
              t('storygame_ai_onboarding_popup_button_watch_ad_then_generate'),
            );
            if (isChecked) {
              setIsEnableAdButton(true);
            }
          } else if (retryCount.current >= 20) {
            // 20번 시도 후에는 중단
            stopAutoRequest();
            setToastText(t('storygame_ai_onboarding_screen_description_no_ad'));
          }
        }
      } else {
        setAdButtonText(
          t('storygame_ai_onboarding_popup_button_watch_ad_then_generate'),
        );
      }
    } else {
      stopAutoRequest();
    }
  }, [
    isAdReady,
    aiProfileInfo?.usedAd,
    aiProfileInfo?.totalAd,
    aiProfileInfo?.latestCreatedAt,
    isChecked,
    status,
    retryCount,
    filter,
  ]);

  const onResetFilter = () => {
    if (aiCharacterPromptOptions) {
      const resetFilterObject = Object.keys(aiCharacterPromptOptions).reduce(
        (acc, key) => {
          acc[key] = undefined;
          return acc;
        },
        {} as Record<string, undefined>,
      );
      setFilter(resetFilterObject);
    }
  };

  const sendAdEvent = (show: boolean) => {
    const playChapter = playChapterInfo?.playChapter;
    const chapterStory = playChapter?.story;
    const storyGenreType = chapterStory?.genreKey?.type;

    let storyGenre: Challenge_Story_Genre | undefined;
    if (
      Object.values(Challenge_Story_Genre).includes(
        storyGenreType as Challenge_Story_Genre,
      )
    ) {
      storyGenre = (storyGenreType as Challenge_Story_Genre) ?? undefined;
    } else {
      storyGenre = undefined;
    }
    const params: {
      storyId: number;
      adUnitId?: string | null;
      adLoc: AdLocationType;
      adUrl?: string | null;
      storyBundleId?: number | null;
      chapterId: number;
      chapterIndex: number;
      storyName: string;
      storyLanguage?: string | null;
      storyGenre?: Challenge_Story_Genre | null;
      playType: ContentType;
      rewardType: 'FREE_CHAPTER' | 'FREE_CHARACTER';
    } = {
      storyId: chapterStory?.storyId ?? -1,
      adUnitId: AD_UNIT_ID,
      adLoc: '캐릭터생성-스토리게임_리워드',
      adUrl: null,
      storyBundleId: chapterStory?.bundle?.storyBundleId ?? undefined,
      chapterId: playChapter?.chapterId ?? -1,
      chapterIndex: playChapter?.chapterIndex ?? -1,
      storyName: chapterStory?.name ?? '',
      storyLanguage: chapterStory?.language?.code ?? undefined,
      storyGenre: storyGenre,
      playType:
        chapterStory?.playType === 'Storygame'
          ? 'storygame'
          : chapterStory?.playType === 'Interactive'
          ? 'interactive'
          : 'novel',
      rewardType: 'FREE_CHARACTER',
    };

    if (show) {
      adStart(params);
    } else {
      adFinish(params);
    }
  };

  const showWebAd = () => {
    showAd();
    sendAdEvent(true);
  };

  const showRewardAd = () => {
    isMobileDevice()
      ? showRewardAdForApp({
          adIdAndroid: process.env.NEXT_PUBLIC_STORYGAME_ADID_ANDROID ?? '',
          adIdiOS: process.env.NEXT_PUBLIC_STORYGAME_ADID_IOS ?? '',
          adsGaParams: {
            rewardType: 'FREE_CHARACTER',
            adLoc: '캐릭터생성-스토리게임_리워드',
            storyId: playChapterInfo?.playChapter.story.storyId ?? undefined,
            storyBundleId:
              playChapterInfo?.playChapter.story.bundle?.storyBundleId ??
              undefined,
            chapterId: playChapterInfo?.playChapter.chapterId ?? -1,
            chapterIndex: playChapterInfo?.playChapter.chapterIndex,
            storyName: playChapterInfo?.playChapter.story.name,
            storyLanguage:
              playChapterInfo?.playChapter.story.language?.code ?? undefined,
            storyGenre: playChapterInfo?.playChapter.story.genreKey?.type,
            playType:
              playChapterInfo?.playChapter.story.playType === 'Storygame'
                ? 'storygame'
                : playChapterInfo?.playChapter.story.playType === 'Interactive'
                ? 'interactive'
                : 'novel',
          },
        })
      : showWebAd();
  };

  const checkboxText = () => {
    const term_key = t('storygame_ai_onboarding_popup_agree_terms_key');
    const regex = new RegExp(`(${term_key})`, 'g');
    return t('storygame_ai_onboarding_popup_agree_terms')
      .split(regex)
      .map((text, index) => {
        if (text === term_key) {
          return isMobileDevice() ? (
            <span
              key={'text' + index}
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() =>
                goExternalPageForApp({
                  link: data?.getNoticeTerms.PP.link,
                })
              }
            >
              {text}
            </span>
          ) : (
            <a
              key={'text' + index}
              href={data?.getNoticeTerms.PP.link}
              target="_blank"
              rel="noreferrer"
              css={labelClick}
            >
              {text}
            </a>
          );
        }
        return text;
      });
  };

  if (selectedFile)
    return (
      <div css={styles}>
        <div className="filter-crop-container">
          <StoryGameAiImageCropPopup
            initialImage={selectedFile}
            onCrop={(croppedImage: File) => {
              onChangeAiProfile({ cropImageFile: croppedImage });
              setSelectedFile(null);
            }}
            onBack={() => {
              setSelectedFile(null);
            }}
          />
        </div>
      </div>
    );
  return (
    <div css={styles}>
      {loading && <SPLoading />}
      <div className="container-style">
        <div className="header">
          <h1>
            {t('storygame_ai_onboarding_popup_title_make_ai_game_profile')}
          </h1>
          <button className="close-button" onClick={onClose}>
            <CloseIconStoryGame />
          </button>
        </div>
        <div className="scrollable-content">
          <div className="filter-content">
            <div className="image-container">
              <img src={preview} alt="AI Generated" />
              <input
                type="file"
                ref={fileInputRef}
                // className="upload-icon-overlay"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept="image/jpeg, image/png, image/webp"
              />
            </div>

            <div className="upload-info-section" onClick={handleUploadClick}>
              <span className="upload-text">
                {t('storygame_ai_onboarding_popup_button_change_photo')}
              </span>
            </div>

            <div className="filter-section">
              <div className="filter-header">
                <div className="filter-title-container">
                  <div className="filter-title">
                    <FilterIconStorygame />
                    <h2>{t('storygame_ai_onboarding_popup_title_filter')}</h2>
                    {Object.values(filter).filter((d) => d).length > 0 && (
                      <span className="purple-color">*</span>
                    )}
                  </div>
                  <p>{t('storygame_ai_onboarding_popup_description_filter')}</p>
                </div>
                <div className="filter-reset" onClick={onResetFilter}>
                  <RefreshIcon />
                  {t('storygame_ai_onboarding_popup_button_reset_filter')}
                </div>
              </div>
              <div className="filter-options">
                {!!aiCharacterPromptOptions &&
                  Object.entries(aiCharacterPromptOptions).map(
                    ([category, options]) => (
                      <FilterCategory
                        key={category}
                        title={category}
                        options={options}
                        selected={filter[category]}
                        onChange={(value) =>
                          handleFilterChange(category, value)
                        }
                      />
                    ),
                  )}
              </div>
            </div>
          </div>
        </div>
        <div className="footer">
          <div css={checkboxStyle}>
            <div css={labelStyle}>
              <div css={checkbox} onClick={handleCheckboxChange}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  {isChecked ? (
                    <>
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        fill="#753BBD"
                      />
                      <path
                        d="M17 8L10 15L7 12"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </>
                  ) : (
                    <rect
                      x="3.75"
                      y="3.75"
                      width="16.5"
                      height="16.5"
                      rx="1.25"
                      stroke="white"
                      strokeOpacity="0.25"
                      strokeWidth="1.5"
                    />
                  )}
                </svg>
              </div>
              <>{checkboxText()}</>
            </div>
          </div>

          {!aiProfileInfo?.latestCreatedAt && areAllValuesEmpty(filter) ? (
            <div className="generate-button-container">
              <button
                className="generate-button free"
                onClick={() => {
                  onCreateAiCharacterSubmit({ isFree: true });
                }}
                disabled={!isChecked}
              >
                {t('storygame_ai_onboarding_popup_button_free_generate')}
              </button>
            </div>
          ) : (
            <div className="generate-button-container">
              <button
                className="generate-button"
                onClick={() => {
                  if (
                    (!requestedTossPaymentsId ||
                      tossPaymentsCompletedData?.ensureTossPaymentsDone.ok ===
                        0) &&
                    !upcId
                  ) {
                    paymentAiCharacter();
                  } else {
                    onCreateAiCharacterSubmit();
                  }
                }}
                disabled={!isChecked}
              >
                {t('storygame_ai_onboarding_popup_button_generate')}
                <>
                  {(!requestedTossPaymentsId ||
                    tossPaymentsCompletedData?.ensureTossPaymentsDone.ok ===
                      0) &&
                    !upcId && (
                      <>
                        {' '}
                        <div className="price-text">
                          {userInfo?.countryCode === 'KR'
                            ? t(
                                'payment_screen_description_price_monetary_unit_for_kr',
                                {
                                  value: insertCommas(
                                    isMobileDevice()
                                      ? MY_CHAR_APP_PRICE.KWR
                                      : paymentProduct?.price ?? 0,
                                  ),
                                },
                              )
                            : t(
                                'payment_screen_description_price_monetary_unit_for_other',
                                {
                                  value: insertCommas(
                                    isMobileDevice()
                                      ? MY_CHAR_APP_PRICE.USD
                                      : paymentProduct?.priceFloat ?? 0,
                                  ),
                                },
                              )}
                        </div>
                      </>
                    )}
                </>
              </button>
              <button
                className="generate-button ad"
                onClick={showRewardAd}
                disabled={!isEnableAdButton}
              >
                {`${adButtonText} `}
                <div className="ad-count">
                  {aiProfileInfo?.usedAd}/{aiProfileInfo?.totalAd}{' '}
                </div>
                <div className="ad-box">AD</div>
              </button>
            </div>
          )}
        </div>
      </div>

      <StoryGameUploadErrorToast
        isShow={isShowErrorMessage}
        message={errorMessage}
        onHide={() => setIsShowErrorMessage(false)}
      />

      {toastText && (
        <div css={toastWrapper}>
          <Toast
            message={toastText}
            onClear={() => {
              setToastText(undefined);
            }}
            customCSS={toastStyle}
          />
        </div>
      )}
    </div>
  );
};

interface FilterCategoryProps {
  title: string;
  options: { id: number; title: string }[];
  selected?: number;
  onChange: (value: number) => void;
}

const FilterCategory: React.FC<FilterCategoryProps> = ({
  title,
  options,
  selected,
  onChange,
}) => (
  <div className="filter-category">
    <div className="title-container">
      <h3>{title} </h3>
      {selected && <span className="purple-color">*</span>}
    </div>
    <div className="options">
      {options.map((option) => (
        <button
          key={option.id}
          className={selected === option.id ? 'selected' : ''}
          onClick={() => onChange(option.id)}
        >
          {option.title}
        </button>
      ))}
    </div>
  </div>
);

const generateButtonStyle = `
  display: flex;
  flex: 1;
  background-color: transparent;
  padding: 1rem;

  color: white;
  border: 1px solid #8b5cf6;
  color: #8b5cf6;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  height:50px;
`;

const styles = css`
  color: white;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${theme.devices.md}px) {
    width: 100vw;
    max-width: 600px;
    height: 100vh;
    ${IS_IOS_SAFARI ? 'max-height: -webkit-fill-available' : ''};
    // @supports (100dvh) {
    //   height: 100dvh;
    // }

    // @supports not (height: 100dvh) {
    //   height: 95vh;
    // }
    height: calc(var(--inner_vh, 1vh) * 100);
  }

  .container-style {
    display: flex;
    flex-direction: column;
    width: 400px;
    max-height: 100vh;
    padding: 0px 0px 20px 0px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.75);

    @media (max-width: ${theme.devices.md}px) {
      width: 100%;
      max-width: 600px;
      height: 100vh;
      padding: 0;
      max-height: 100vh;
      ${IS_IOS_SAFARI ? 'max-height: -webkit-fill-available' : ''};
      height: calc(var(--inner_vh, 1vh) * 100);
    }

    // @supports (100dvh) {
    //   height: 100dvh;
    // }
    // @supports not (height: 100dvh) {
    //   height: 95vh;
    // }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 0px 20px;
    min-height: 66px;

    h1 {
      color: #ffffffe5;
      font-size: 18px;
      font-weight: 700;
      line-height: 26px; /* 144.444% */
    }

    .close-button {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
    }
  }

  .scrollable-content {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .filter-content {
    width: 100%;
    height: 100%;
    background-color: transparent;
    display: flex;
    flex-direction: column;

    align-items: center;
    position: relative;
    border-radius: 0.625rem;

    @media (max-width: ${theme.devices.md}px) {
      height: 100vh;

      // @supports (100dvh) {
      //   height: 100dvh;
      // }
      // @supports not (height: 100dvh) {
      //   height: 95vh;
      // }
      height: calc(var(--inner_vh, 1vh) * 100);
    }
  }

  .image-container {
    display: flex;
    flex-direction: column;
    position: relative;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;

    .upload-icon-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.5);
      opacity: 0;
      transition: opacity 0.3s ease;
      border-radius: 10px;
      cursor: pointer;
    }
    &:hover .upload-icon-overlay {
      opacity: 1;
    }

    img {
      width: 96px;
      height: 96px;
      object-fit: cover;
      border-radius: 10px;
    }
    img:hover {
      background-color: #ffffff1a;
    }

    p {
      font-size: 14px;
      text-align: center;
    }
  }

  .upload-info-section {
    display: flex;
    margin-bottom: 20px;
    border-radius: var(--radius-xs, 4px);
    border: 1px solid var(--whiteA50, rgba(255, 255, 255, 0.5));

    display: flex;
    height: 34px;
    padding: var(--spacing-s, 8px) var(--spacing-ms, 12px);
    justify-content: center;
    align-items: center;
    cursor: pointer;

    .upload-text {
      color: #fff;
      text-align: center;
      font-size: 13px;
      font-weight: 400;
      line-height: 18px; /* 138.462% */
    }
  }

  .filter-section {
    width: 100%;
    border-top: 1px solid #ffffff1a;
    padding: 20px;

    .filter-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      display: flex;
      padding: 4px 0px;
      align-items: center;
      gap: 4px;
    }

    .filter-reset {
      display: flex;
      align-items: center;
      color: #ffffff80;
      text-align: center;
      gap: 4px;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      cursor: pointer;
    }

    .filter-title {
      font-size: 18px;
      margin-bottom: 5px;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    p {
      color: var(--whiteA50, rgba(255, 255, 255, 0.5));

      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      margin-bottom: 8px;
    }
  }

  .filter-options {
    display: flex;
    padding: var(--spacing-s, 8px) var(--spacing-m, 16px);
    flex-direction: column;

    border-radius: var(--radius-sm, 6px);
    background: var(--whiteA05, rgba(255, 255, 255, 0.05));
  }

  .filter-category {
    display: flex;
    padding: var(--spacing-s, 8px) 0px;
    align-items: center;
    align-self: stretch;

    .title-container {
      display: flex;
      flex-direction: row;
      flex: 1;
    }
    h3 {
      font-size: 16px;
    }

    .options {
      display: flex;
      padding: 6px 8px;
      justify-content: center;
      align-items: center;
      gap: 10px;

      button {
        border-radius: 4px;
        border: 1px solid #ffffff1a;
        background-color: #333;

        padding: 5px 10px;
        cursor: pointer;
        font-size: 14px;

        color: #ffffffbf;

        font-size: 14px;
        font-weight: 400;

        &.selected {
          background-color: #000000bf;
          border-color: #9457eb;
        }
      }
    }
  }

  .footer {
    padding: 0px 20px;
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    margin-bottom: env(safe-area-inset-bottom);
  }
  .generate-button-container {
    display: flex;
    justify-content: space-between;
    gap: 6px;

    @media (max-width: ${theme.devices.md}px) {
      padding: 0 0 20px 0;
    }
  }

  .generate-button {
    ${generateButtonStyle}

    &.ad {
      background-color: #753bbd;
      border-color: #753bbd;
      color: #fff;
    }
    &.ad:disabled {
      background: #ffffff66;
      border: 1px solid transparent;
      cursor: not-allowed;
    }

    &.free {
      background-color: #753bbd;
      border-color: #753bbd;
      color: #fff;
    }

    .ad-count {
      font-size: 12px;
    }

    .ad-box {
      border-radius: var(--radius-xxs, 2px);
      border: 1px solid var(--whiteA50, rgba(255, 255, 255, 0.5));
      display: flex;
      padding: var(--spacing-xxs, 2px) var(--spacing-xs, 4px);
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 10px;
    }
  }

  .generate-button:disabled {
    ${generateButtonStyle}

    background-color: transparent;
    border: 1px solid #ffffff66;
    color: #ffffff;
    opacity: 0.25;
    cursor: not-allowed;
  }

  .upload-button {
    width: 100%;
    padding: 1rem;
    background-color: #8b5cf6;
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-size: 1rem;
    cursor: pointer;
  }

  .upload-button:hover {
    background-color: #7c3aed;
  }

  .filter-crop-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @media (max-width: 1024px) {
      height: 100%;
      width: 100%;

      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100vh;
      justify-content: center;
      align-items: center;
      display: flex;
      max-height: calc(var(--inner_vh, 1vh) * 100);
      @supports (100dvh) {
        height: 100dvh;
      }
      @supports not (height: 100dvh) {
        height: 95vh;
      }
    }
  }

  .purple-color {
    color: #9457eb;
  }

  .price-text {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 6px;

    .origin {
      font: ${theme.font.body.body1};
      text-decoration-line: line-through;
      color: ${theme.colors.gray500};
    }
  }
`;

export default StoryGameAiImageFilterPopup;
const checkboxStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const checkbox = css`
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const labelStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--whiteA50, rgba(255, 255, 255, 0.5));
  font-size: 14px;
  margin: 0px 0px 12px;
`;

const labelClick = css`
  cursor: pointer;
  text-decoration: underline;
`;

const toastStyle = css`
  bottom: 20rem;
  color: ${theme.colors.white};
  background: #000000;
  border-radius: 0.5em;
`;

const toastWrapper = css`
  position: fixed;
  display: flex;
  top: 0;
  left: 0;
  width: 100%;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;
